import { useState, useEffect, useRef } from 'react'
import type { MetaFunction, ActionFunctionArgs, LoaderFunctionArgs } from 'react-router'
import { Form, useActionData, useLoaderData, Link, useSearchParams, useNavigation } from 'react-router'
import { scoreLead } from '~/lib/lead-quality.server'
import { db } from '@afficheservices/db'
import { leads, leadImages, serviceCategories } from '@afficheservices/db/schema'
import { eq, gte, sql } from 'drizzle-orm'
import {
  Wrench, Zap, Paintbrush, Hammer, Home, Key, Snowflake, Truck,
  SprayCan, TreePine, Scissors, Shield, ShieldCheck, CheckCircle, ArrowRight,
  ArrowLeft, Clock, Users, Star, Phone, MapPin, Building2, Camera,
} from 'lucide-react'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ExitIntentModal } from '~/components/exit-intent'
import { DYNAMIC_PAGE_HEADERS } from '~/lib/cache.server'
import { checkRateLimit, rateLimitResponse } from '~/lib/security.server'

export function headers() { return DYNAMIC_PAGE_HEADERS }

export async function loader(_args: LoaderFunctionArgs) {
  // Real count of devis submitted in the last 7 days — replaces the
  // previous Math.random() badge that broke trust on F5 (audit-02).
  const sevenDaysAgo = new Date()
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
  const [row] = await db
    .select({ count: sql<number>`count(*)::int` })
    .from(leads)
    .where(gte(leads.createdAt, sevenDaysAgo))
  return { recentDevisCount: row?.count ?? 0 }
}

export const meta: MetaFunction = () => [
  { title: 'Devis travaux gratuit — 3 devis vérifiés sous 48h' },
  { name: 'description', content: 'Décrivez votre projet en 30 secondes. Recevez 3 devis gratuits d\'artisans SIRET-vérifiés et assurés RC Pro sous 48h. Plomberie, électricité, rénovation, isolation. Sans inscription.' },
  { name: 'robots', content: 'index, follow' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/devis-gratuit' },
  { property: 'og:title', content: 'Devis travaux gratuit — 3 devis vérifiés sous 48h' },
  { property: 'og:description', content: 'Décrivez votre projet en 30 secondes. Recevez 3 devis gratuits d\'artisans SIRET-vérifiés sous 48h.' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/devis-gratuit' },
  { property: 'og:type', content: 'website' },
]

// ─── Service definitions with sub-questions ──────────────────

interface ServiceDef {
  slug: string
  name: string
  icon: React.ComponentType<{ className?: string }>
  emoji: string
  color: string
  activeColor: string
  subQuestions: SubQuestion[]
}

interface SubQuestion {
  id: string
  question: string
  options: QuizOption[]
  multiSelect?: boolean
}

interface QuizOption {
  value: string
  label: string
  sublabel?: string
  icon?: React.ComponentType<{ className?: string }>
  emoji?: string
}

const SERVICES: ServiceDef[] = [
  {
    slug: 'plomberie', name: 'Plomberie', icon: Wrench, emoji: '🔧',
    color: 'border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-400 hover:bg-blue-100',
    activeColor: 'border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de travaux ?', options: [
        { value: 'fuite', label: 'Réparation fuite', emoji: '💧' },
        { value: 'debouchage', label: 'Débouchage', emoji: '🚿' },
        { value: 'chauffe-eau', label: 'Chauffe-eau', emoji: '🔥' },
        { value: 'salle-de-bain', label: 'Salle de bain', emoji: '🛁' },
        { value: 'robinetterie', label: 'Robinetterie', emoji: '🚰' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
      { id: 'property', question: 'Type de logement', options: [
        { value: 'appartement', label: 'Appartement', icon: Building2 },
        { value: 'maison', label: 'Maison', icon: Home },
        { value: 'commerce', label: 'Local commercial', icon: Building2 },
      ]},
    ],
  },
  {
    slug: 'electricite', name: 'Électricité', icon: Zap, emoji: '⚡',
    color: 'border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100',
    activeColor: 'border-amber-600 bg-amber-600 text-white shadow-lg shadow-amber-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de travaux ?', options: [
        { value: 'installation', label: 'Nouvelle installation', emoji: '🔌' },
        { value: 'renovation', label: 'Rénovation électrique', emoji: '🏗️' },
        { value: 'depannage', label: 'Dépannage / Panne', emoji: '⚠️' },
        { value: 'tableau', label: 'Mise aux normes', emoji: '📊' },
        { value: 'eclairage', label: 'Éclairage', emoji: '💡' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
      { id: 'property', question: 'Type de logement', options: [
        { value: 'appartement', label: 'Appartement', icon: Building2 },
        { value: 'maison', label: 'Maison', icon: Home },
        { value: 'commerce', label: 'Local commercial', icon: Building2 },
      ]},
    ],
  },
  {
    slug: 'peinture', name: 'Peinture', icon: Paintbrush, emoji: '🎨',
    color: 'border-purple-200 bg-purple-50 text-purple-700 hover:border-purple-400 hover:bg-purple-100',
    activeColor: 'border-purple-600 bg-purple-600 text-white shadow-lg shadow-purple-200',
    subQuestions: [
      { id: 'type', question: 'Que souhaitez-vous peindre ?', options: [
        { value: 'murs-interieurs', label: 'Murs intérieurs', emoji: '🏠' },
        { value: 'plafond', label: 'Plafond', emoji: '⬆️' },
        { value: 'facade', label: 'Façade extérieure', emoji: '🏢' },
        { value: 'boiseries', label: 'Boiseries / Portes', emoji: '🚪' },
        { value: 'papier-peint', label: 'Papier peint', emoji: '📜' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
      { id: 'surface', question: 'Surface approximative', options: [
        { value: 'petite', label: 'Moins de 20 m²', sublabel: '1 pièce' },
        { value: 'moyenne', label: '20 à 50 m²', sublabel: '2-3 pièces' },
        { value: 'grande', label: '50 à 100 m²', sublabel: 'Appartement' },
        { value: 'tres-grande', label: 'Plus de 100 m²', sublabel: 'Maison entière' },
      ]},
    ],
  },
  {
    slug: 'renovation', name: 'Rénovation', icon: Hammer, emoji: '🏗️',
    color: 'border-orange-200 bg-orange-50 text-orange-700 hover:border-orange-400 hover:bg-orange-100',
    activeColor: 'border-orange-600 bg-orange-600 text-white shadow-lg shadow-orange-200',
    subQuestions: [
      { id: 'type', question: 'Type de rénovation', options: [
        { value: 'salle-de-bain', label: 'Salle de bain', emoji: '🛁' },
        { value: 'cuisine', label: 'Cuisine', emoji: '🍳' },
        { value: 'complete', label: 'Rénovation complète', emoji: '🏠' },
        { value: 'sol', label: 'Sol / Parquet', emoji: '🪵' },
        { value: 'isolation', label: 'Isolation', emoji: '🧱' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
      { id: 'surface', question: 'Surface du projet', options: [
        { value: 'petite', label: 'Moins de 10 m²', sublabel: 'Petite pièce' },
        { value: 'moyenne', label: '10 à 30 m²', sublabel: 'Pièce standard' },
        { value: 'grande', label: '30 à 80 m²', sublabel: 'Appartement' },
        { value: 'tres-grande', label: 'Plus de 80 m²', sublabel: 'Grande surface' },
      ]},
    ],
  },
  {
    slug: 'menuiserie', name: 'Menuiserie', icon: Hammer, emoji: '🪚',
    color: 'border-yellow-200 bg-yellow-50 text-yellow-700 hover:border-yellow-400 hover:bg-yellow-100',
    activeColor: 'border-yellow-600 bg-yellow-600 text-white shadow-lg shadow-yellow-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de menuiserie ?', options: [
        { value: 'fenetre', label: 'Fenêtres', emoji: '🪟' },
        { value: 'porte', label: 'Portes', emoji: '🚪' },
        { value: 'placard', label: 'Placard sur mesure', emoji: '🗄️' },
        { value: 'escalier', label: 'Escalier', emoji: '🪜' },
        { value: 'terrasse', label: 'Terrasse bois', emoji: '🌿' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
    ],
  },
  {
    slug: 'toiture', name: 'Toiture', icon: Home, emoji: '🏠',
    color: 'border-red-200 bg-red-50 text-red-700 hover:border-red-400 hover:bg-red-100',
    activeColor: 'border-red-600 bg-red-600 text-white shadow-lg shadow-red-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de travaux ?', options: [
        { value: 'reparation', label: 'Réparation / Fuite', emoji: '💧' },
        { value: 'refection', label: 'Réfection complète', emoji: '🔨' },
        { value: 'nettoyage', label: 'Nettoyage / Démoussage', emoji: '🧹' },
        { value: 'isolation', label: 'Isolation toiture', emoji: '🧱' },
        { value: 'gouttiere', label: 'Gouttières', emoji: '🌧️' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
    ],
  },
  {
    slug: 'serrurerie', name: 'Serrurerie', icon: Key, emoji: '🔐',
    color: 'border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-400 hover:bg-slate-100',
    activeColor: 'border-slate-600 bg-slate-600 text-white shadow-lg shadow-slate-200',
    subQuestions: [
      { id: 'type', question: 'Quel type d\'intervention ?', options: [
        { value: 'ouverture', label: 'Ouverture de porte', emoji: '🚪' },
        { value: 'serrure', label: 'Changement serrure', emoji: '🔒' },
        { value: 'blindage', label: 'Blindage de porte', emoji: '🛡️' },
        { value: 'cle', label: 'Double de clé', emoji: '🔑' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
    ],
  },
  {
    slug: 'climatisation', name: 'Climatisation', icon: Snowflake, emoji: '❄️',
    color: 'border-cyan-200 bg-cyan-50 text-cyan-700 hover:border-cyan-400 hover:bg-cyan-100',
    activeColor: 'border-cyan-600 bg-cyan-600 text-white shadow-lg shadow-cyan-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de travaux ?', options: [
        { value: 'installation', label: 'Installation clim', emoji: '❄️' },
        { value: 'entretien', label: 'Entretien annuel', emoji: '🔧' },
        { value: 'pac', label: 'Pompe à chaleur', emoji: '♻️' },
        { value: 'vmc', label: 'VMC', emoji: '💨' },
        { value: 'chauffage', label: 'Chauffage', emoji: '🔥' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
    ],
  },
  {
    slug: 'nettoyage', name: 'Nettoyage', icon: SprayCan, emoji: '🧹',
    color: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-400 hover:bg-emerald-100',
    activeColor: 'border-emerald-600 bg-emerald-600 text-white shadow-lg shadow-emerald-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de nettoyage ?', options: [
        { value: 'appartement', label: 'Nettoyage appartement', emoji: '🏠' },
        { value: 'fin-chantier', label: 'Fin de chantier', emoji: '🏗️' },
        { value: 'vitrerie', label: 'Vitrerie', emoji: '🪟' },
        { value: 'moquette', label: 'Moquette / Tapis', emoji: '🧶' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
    ],
  },
  {
    slug: 'jardinage', name: 'Jardinage', icon: TreePine, emoji: '🌿',
    color: 'border-green-200 bg-green-50 text-green-700 hover:border-green-400 hover:bg-green-100',
    activeColor: 'border-green-600 bg-green-600 text-white shadow-lg shadow-green-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de travaux ?', options: [
        { value: 'tonte', label: 'Tonte pelouse', emoji: '🌱' },
        { value: 'haie', label: 'Taille de haie', emoji: '✂️' },
        { value: 'elagage', label: 'Élagage', emoji: '🌳' },
        { value: 'creation', label: 'Création jardin', emoji: '🌺' },
        { value: 'terrasse', label: 'Terrasse', emoji: '🪵' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
    ],
  },
  {
    slug: 'demenagement', name: 'Déménagement', icon: Truck, emoji: '📦',
    color: 'border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-400 hover:bg-indigo-100',
    activeColor: 'border-indigo-600 bg-indigo-600 text-white shadow-lg shadow-indigo-200',
    subQuestions: [
      { id: 'type', question: 'Type de déménagement', options: [
        { value: 'studio', label: 'Studio / T1', emoji: '🏠' },
        { value: 't2-t3', label: 'T2 / T3', emoji: '🏡' },
        { value: 't4-plus', label: 'T4+ / Maison', emoji: '🏘️' },
        { value: 'bureau', label: 'Bureau / Commerce', emoji: '🏢' },
        { value: 'objet-lourd', label: 'Objet lourd (piano...)', emoji: '🎹' },
      ]},
    ],
  },
  {
    slug: 'carrelage-sol', name: 'Carrelage', icon: Scissors, emoji: '🔲',
    color: 'border-teal-200 bg-teal-50 text-teal-700 hover:border-teal-400 hover:bg-teal-100',
    activeColor: 'border-teal-600 bg-teal-600 text-white shadow-lg shadow-teal-200',
    subQuestions: [
      { id: 'type', question: 'Quel type de carrelage ?', options: [
        { value: 'sol', label: 'Sol intérieur', emoji: '🏠' },
        { value: 'mural', label: 'Mural (cuisine/SDB)', emoji: '🛁' },
        { value: 'exterieur', label: 'Extérieur / Terrasse', emoji: '🌿' },
        { value: 'mosaique', label: 'Mosaïque', emoji: '🎨' },
        { value: 'autre', label: 'Autre', emoji: '📋' },
      ]},
      { id: 'surface', question: 'Surface approximative', options: [
        { value: 'petite', label: 'Moins de 10 m²', sublabel: '1 pièce' },
        { value: 'moyenne', label: '10 à 30 m²', sublabel: '2-3 pièces' },
        { value: 'grande', label: '30 à 60 m²', sublabel: 'Grande surface' },
        { value: 'tres-grande', label: 'Plus de 60 m²', sublabel: 'Maison entière' },
      ]},
    ],
  },
]

const URGENCIES = [
  { value: 'urgent_24h', label: 'Urgent', sublabel: 'Dans les 24h', emoji: '🔴', color: 'border-red-200 bg-red-50 text-red-700', activeColor: 'border-red-600 bg-red-600 text-white shadow-lg shadow-red-200' },
  { value: 'within_days', label: 'Cette semaine', sublabel: 'Quelques jours', emoji: '🟠', color: 'border-amber-200 bg-amber-50 text-amber-700', activeColor: 'border-amber-600 bg-amber-600 text-white shadow-lg shadow-amber-200' },
  { value: 'within_week', label: 'Ce mois-ci', sublabel: 'Pas pressé', emoji: '🔵', color: 'border-blue-200 bg-blue-50 text-blue-700', activeColor: 'border-blue-600 bg-blue-600 text-white shadow-lg shadow-blue-200' },
  { value: 'flexible', label: 'Je compare', sublabel: 'Pas de date fixe', emoji: '⚪', color: 'border-slate-200 bg-slate-50 text-slate-700', activeColor: 'border-slate-600 bg-slate-600 text-white shadow-lg shadow-slate-200' },
]

const BUDGETS = [
  { value: 'under_500', label: 'Moins de 500 €', sublabel: 'Petit projet' },
  { value: '500_1000', label: '500 — 1 000 €', sublabel: 'Projet moyen' },
  { value: '1000_2500', label: '1 000 — 2 500 €', sublabel: 'Projet important' },
  { value: '2500_5000', label: '2 500 — 5 000 €', sublabel: 'Gros projet' },
  { value: '5000_10000', label: '5 000 — 10 000 €', sublabel: 'Rénovation' },
  { value: 'over_10000', label: 'Plus de 10 000 €', sublabel: 'Projet majeur' },
  { value: 'unknown', label: 'Je ne sais pas encore', sublabel: 'À estimer' },
]

// ─── Action ──────────────────────────────────────────────────

export async function action({ request }: ActionFunctionArgs) {
  if (!checkRateLimit(request, 'form')) return rateLimitResponse()

  const form = await request.formData()
  const service = form.get('service') as string
  const urgency = form.get('urgency') as string
  const budget = form.get('budget') as string
  const postalCode = form.get('postalCode') as string
  const phone = form.get('phone') as string
  const name = form.get('name') as string
  const email = form.get('email') as string
  const description = form.get('description') as string
  const subAnswers = form.get('subAnswers') as string

  if (!service || !postalCode || !phone || !email || !name) {
    return { error: 'Veuillez remplir tous les champs obligatoires.' }
  }
  // Cheap server-side email shape check — doesn't replace verification but
  // catches obvious typos / honeypots before we touch the DB.
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { error: 'Veuillez entrer une adresse email valide.' }
  }
  // French postal code shape + department range. The pattern attribute on
  // the input is client-side only, so users (and bots) can POST "00000",
  // which would create a lead with no resolvable city (audit #17). Valid
  // metropolitan departments are 01-95, DOM 97 (971-976), Monaco 98 (98000).
  if (!/^\d{5}$/.test(postalCode)) {
    return { error: 'Code postal invalide.' }
  }
  const dep = parseInt(postalCode.slice(0, 2), 10)
  const isValidDep = (dep >= 1 && dep <= 95) || dep === 97 || dep === 98
  if (!isValidDep) {
    return { error: 'Code postal invalide.' }
  }

  const [cat] = await db.select({ id: serviceCategories.id, name: serviceCategories.nameFr })
    .from(serviceCategories).where(eq(serviceCategories.slug, service)).limit(1)

  // Hard-fail if the slug doesn't resolve to a category. Previously we
  // wrote `categoryId: cat?.id ?? null!` which used a non-null cast and let
  // a NULL through to the DB, where the NOT NULL constraint blew up far
  // from the source (audit #18). Reject early with a clear error.
  if (!cat) {
    return { error: 'Service inconnu. Veuillez recommencer.' }
  }

  const ref = `AFF-${Date.now().toString(36).toUpperCase()}`
  // Untrusted client input — bots can POST anything (audit #6).
  let detailParts: Record<string, unknown> = {}
  if (subAnswers) {
    try {
      const parsed = JSON.parse(subAnswers)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        detailParts = parsed
      }
    } catch { /* ignore — fall back to empty */ }
  }
  const fullDescription = [
    description || `Demande: ${cat?.name ?? service}`,
    ...Object.entries(detailParts).map(([k, v]) => `${k}: ${v}`),
  ].join(' · ')

  // Geocode postal code for city + coordinates
  let city = ''
  let latitude = '48.8566'
  let longitude = '2.3522'
  let departmentCode = postalCode?.substring(0, 2) ?? '75'
  try {
    // 3s timeout — geocoding API has had multi-second outages historically;
    // we'd rather lose city resolution than block the lead submission for 30s.
    const geoRes = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${postalCode}&type=municipality&limit=1`,
      { signal: AbortSignal.timeout(3000) },
    )
    const geoData = await geoRes.json() as any
    if (geoData?.features?.[0]) {
      const f = geoData.features[0]
      city = f.properties?.city ?? f.properties?.name ?? ''
      latitude = String(f.geometry?.coordinates?.[1] ?? 48.8566)
      longitude = String(f.geometry?.coordinates?.[0] ?? 2.3522)
      departmentCode = f.properties?.context?.split(',')?.[0]?.trim() ?? postalCode.substring(0, 2)
    }
  } catch { /* use defaults */ }

  // Anti-fraud / quality gate. Rejects spam, bot floods, and obviously
  // invalid leads before they hit the DB and burn pro credits.
  const clientIp = request.headers.get('cf-connecting-ip') ?? request.headers.get('x-forwarded-for')?.split(',')[0]?.trim() ?? null
  const quality = await scoreLead({
    email,
    phone,
    description: fullDescription,
    postalCode,
    ipAddress: clientIp,
    turnstileToken: form.get('cf-turnstile-response') as string | null,
  })
  if (quality.reject) {
    console.warn('[devis] rejected lead', { score: quality.score, flags: quality.flags, ip: clientIp })
    return { error: 'Demande non valide. Réessayez ou contactez le support.' }
  }

  const [newLead] = await db.insert(leads).values({
    referenceNumber: ref,
    categoryId: cat.id,
    title: cat.name,
    description: fullDescription,
    postalCode,
    city,
    departmentCode,
    latitude,
    longitude,
    guestPhone: phone,
    guestEmail: email || '',
    guestFirstName: name || '',
    urgency: (urgency as any) || 'flexible',
    budgetRange: (budget as any) || 'unknown',
    propertyType: 'other',
    source: 'web_form',
    status: 'submitted',
    submittedAt: new Date(),
  }).returning({ id: leads.id })

  // Save uploaded photos to lead_images
  // FormData entries can be `File` even on text fields if a client posts
  // multipart/form-data with a file under that name. Guard the type before
  // assuming string (audit #19) — and validate the parsed array shape too.
  const photoUrlsRaw = form.get('photoUrls')
  if (typeof photoUrlsRaw === 'string' && photoUrlsRaw && newLead?.id) {
    try {
      const parsed = JSON.parse(photoUrlsRaw)
      if (Array.isArray(parsed)) {
        const urls = parsed.filter((u): u is string => typeof u === 'string' && u.length > 0).slice(0, 10)
        for (let i = 0; i < urls.length; i++) {
          await db.insert(leadImages).values({ leadId: newLead.id, imageUrl: urls[i], displayOrder: i })
        }
      }
    } catch { /* ignore parse errors */ }
  }

  return { success: true, ref }
}

// ─── Component ───────────────────────────────────────────────

export default function DevisGratuit() {
  const [searchParams] = useSearchParams()
  const preselectedService = searchParams.get('service') ?? ''
  const preselectedPostal = searchParams.get('cp') ?? ''
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const isSubmitting = navigation.state === 'submitting'

  // Quiz state
  const [step, setStep] = useState(preselectedService ? 2 : 1)
  const [selectedService, setSelectedService] = useState(preselectedService)
  const [subAnswers, setSubAnswers] = useState<Record<string, string>>({})
  const [_currentSubQ, setCurrentSubQ] = useState(0)
  const [selectedUrgency, setSelectedUrgency] = useState('')
  const [selectedBudget, setSelectedBudget] = useState('')
  const [description, setDescription] = useState('')
  const [photos, setPhotos] = useState<Array<{ name: string; url: string; file: File; uploaded?: string }>>([])

  const serviceDef = SERVICES.find(s => s.slug === selectedService)

  // Refactored to 3 steps (was 5-9 depending on service): biggest friction reducer.
  // Step 1: service. Step 2: combined urgency + budget + description. Step 3: contact.
  // Sub-questions dropped — optional service metadata, not mandatory steps.
  const STEP_SERVICE = 1
  const STEP_DETAILS = 2
  const STEP_CONTACT = 3
  const TOTAL_STEPS = STEP_CONTACT

  const progressPercent = Math.round((step / TOTAL_STEPS) * 100)

  // Animated step transition + history stack so the back buttons always
  // pop the *actual* previous step, not a hardcoded one. Hardcoded fallbacks
  // (e.g. `goToStep(STEP_URGENCY)`) broke when the user landed on a step
  // via ?service=... or jumped over an empty sub-questions section
  // (audit #18). With a stack, "Retour" always undoes the last forward move.
  const [animating, setAnimating] = useState(false)
  const stepHistoryRef = useRef<number[]>([])
  function goToStep(n: number) {
    setAnimating(true)
    stepHistoryRef.current.push(step)
    setTimeout(() => { setStep(n); setAnimating(false) }, 150)
  }
  function goBack(fallback: number) {
    setAnimating(true)
    const prev = stepHistoryRef.current.pop() ?? fallback
    setTimeout(() => { setStep(prev); setAnimating(false) }, 150)
  }

  // Real social proof — count of devis submitted in the last 7 days,
  // computed in the loader. Was previously Math.random() which broke on F5.
  const { recentDevisCount } = useLoaderData<typeof loader>()

  // Auto-detect city from postal code + fetch dept devis count for social proof
  const [detectedCity, setDetectedCity] = useState('')
  const [deptDevisCount, setDeptDevisCount] = useState(0)
  function handlePostalCodeChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5)
    e.target.value = val
    if (val.length === 5) {
      fetch(`https://api-adresse.data.gouv.fr/search/?q=${val}&type=municipality&limit=1`)
        .then(r => r.json())
        .then(data => {
          const city = data.features?.[0]?.properties?.city
          if (city) setDetectedCity(city)
        })
        .catch(() => {})
      const dept = val.slice(0, 2)
      fetch(`/api/devis-count?dept=${dept}`)
        .then(r => r.json())
        .then((data: { count?: number }) => setDeptDevisCount(data.count ?? 0))
        .catch(() => setDeptDevisCount(0))
    } else {
      setDetectedCity('')
      setDeptDevisCount(0)
    }
  }

  // Phone formatting
  function handlePhoneChange(e: React.ChangeEvent<HTMLInputElement>) {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
    const formatted = digits.replace(/(\d{2})(?=\d)/g, '$1 ').trim()
    e.target.value = formatted
  }

  // Step labels for header
  function getStepLabel(): string {
    if (step === STEP_SERVICE) return 'Service'
    if (step === STEP_DETAILS) return 'Détails'
    if (step === STEP_CONTACT) return 'Contact'
    return ''
  }

  // ─── Success screen ──────────────────────────────────────

  // Clear the abandoned-quiz banner once a real lead lands. Without this,
  // the user submits successfully but the homepage's "Reprenez votre devis"
  // banner keeps re-appearing on next visits (audit #19).
  useEffect(() => {
    if (actionData?.success) {
      try { sessionStorage.removeItem('devis_progress') } catch {}
    }
  }, [actionData?.success])

  if (actionData?.success) {
    return (
      <>
        <Navbar />
        <main className="flex min-h-[70vh] flex-col items-center justify-center px-4 py-16">
          <div className="mx-auto max-w-md text-center">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 animate-[bounce_0.5s_ease-out]">
              <CheckCircle className="h-10 w-10 text-emerald-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Demande envoyée !</h1>
            <p className="mt-2 text-slate-500">Réf. <strong className="font-mono text-primary">{actionData.ref}</strong></p>

            {/* What happens next — timeline */}
            <div className="mt-8 space-y-0 text-left">
              {[
                { icon: CheckCircle, color: 'bg-emerald-100 text-emerald-600', title: 'Demande reçue', desc: 'Votre projet est enregistré', done: true },
                { icon: Users, color: 'bg-blue-100 text-blue-600', title: 'Recherche d\'artisans', desc: 'Nous cherchons les pros dans votre zone', done: false },
                { icon: Phone, color: 'bg-amber-100 text-amber-600', title: 'Jusqu\'à 3 devis', desc: 'Les artisans vous contactent sous 48h', done: false },
                { icon: Star, color: 'bg-purple-100 text-purple-600', title: 'Comparez et choisissez', desc: 'Vous choisissez le meilleur artisan', done: false },
              ].map((item, i) => {
                const ItemIcon = item.icon
                return (
                  <div key={i} className="flex items-start gap-4 py-3">
                    <div className="relative flex flex-col items-center">
                      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${item.color} ${item.done ? '' : 'opacity-60'}`}>
                        <ItemIcon className="h-5 w-5" />
                      </div>
                      {i < 3 && <div className={`mt-1 h-6 w-px ${item.done ? 'bg-emerald-300' : 'bg-slate-200'}`} />}
                    </div>
                    <div className="pt-1">
                      <p className={`text-sm font-semibold ${item.done ? 'text-slate-900' : 'text-slate-500'}`}>{item.title}</p>
                      <p className="text-xs text-slate-400">{item.desc}</p>
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-8 space-y-3">
              <Link to="/" className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-white shadow-md hover:bg-primary/90">
                Retour à l'accueil <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/guide-prix" className="block text-sm text-primary hover:underline">Consulter le guide des prix →</Link>
            </div>
          </div>
        </main>
        {/* Trust signals on quiz page */}
        <section className="border-t border-slate-100 bg-surface-secondary py-10">
          <div className="mx-auto max-w-2xl px-4 text-center">
            <div className="flex flex-wrap items-center justify-center gap-6 text-xs text-slate-500">
              <span className="flex items-center gap-1.5">✓ Service 100% gratuit</span>
              <span className="flex items-center gap-1.5">✓ Artisans vérifiés (SIRET + assurances)</span>
              <span className="flex items-center gap-1.5">✓ Jusqu'à 3 devis sous 48h</span>
              <span className="flex items-center gap-1.5">✓ Sans engagement</span>
            </div>
            <p className="mt-4 text-xs text-slate-400">Vos données sont protégées conformément au RGPD. Elles ne sont transmises qu'aux artisans correspondant à votre demande.</p>
          </div>
        </section>

        <Footer />
      </>
    )
  }

  // ─── Quiz ────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <ExitIntentModal currentStep={step} totalSteps={TOTAL_STEPS} />
      {/* Minimal header */}
      <header className="flex h-14 items-center justify-between border-b border-slate-100 bg-white px-4">
        <Link to="/" className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary">
            <Wrench className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-lg font-bold text-slate-900">Affiche<span className="text-primary">Services</span></span>
        </Link>
        <span className="text-xs text-slate-400">{getStepLabel()} · {step}/{TOTAL_STEPS}</span>
      </header>

      {/* Progress bar — thin, animated */}
      <div className="h-1 w-full bg-slate-100">
        <div className="h-full bg-primary transition-all duration-500 ease-out" style={{ width: `${progressPercent}%` }} />
      </div>

      <main className="mx-auto max-w-2xl px-4 py-8 sm:py-12">
        <div className={`transition-all duration-150 ${animating ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}>

          {/* ═══ STEP 1: Service selection ═══ */}
          {step === STEP_SERVICE && (
            <div>
              <h1 className="text-center text-2xl font-bold text-slate-900 sm:text-3xl">
                De quel service avez-vous besoin ?
              </h1>
              <p className="mt-2 text-center text-sm text-slate-500">
                3 devis gratuits sous 48h · Artisans SIRET-vérifiés et assurés RC Pro
              </p>

              {/* Real social proof — devis envoyés sur les 7 derniers jours */}
              {recentDevisCount > 0 && (
                <div className="mx-auto mt-4 flex items-center justify-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-xs text-emerald-700">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                  </span>
                  <strong>{recentDevisCount}</strong> demande{recentDevisCount > 1 ? 's' : ''} de devis cette semaine
                </div>
              )}

              {/* Unified card style — same monochrome look as the rest of
                  the site. Per-service pastel palette removed (felt childish
                  next to the corporate slate/blue UI). */}
              <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
                {SERVICES.map((s) => {
                  const Icon = s.icon
                  const active = selectedService === s.slug
                  return (
                    <button
                      key={s.slug}
                      onClick={() => { setSelectedService(s.slug); setSubAnswers({}); setCurrentSubQ(0); goToStep(STEP_DETAILS) }}
                      className={`group flex flex-col items-center gap-3 rounded-2xl border-2 p-5 text-center transition-all ${
                        active
                          ? 'border-primary bg-primary text-white shadow-lg shadow-primary/20'
                          : 'border-slate-200 bg-white text-slate-700 hover:border-primary/40 hover:bg-primary/5'
                      }`}
                    >
                      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${active ? 'bg-white/15' : 'bg-primary/10 text-primary'}`}>
                        <Icon className="h-5 w-5" />
                      </span>
                      <span className="text-sm font-semibold">{s.name}</span>
                    </button>
                  )
                })}
              </div>

              {/* Trust */}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-5 text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><Shield className="h-4 w-4 text-emerald-500" aria-hidden="true" /> 100% gratuit</span>
                <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-blue-500" aria-hidden="true" /> Réponse 48h</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-primary" aria-hidden="true" /> Sans engagement</span>
              </div>
              <p className="mt-3 flex items-center justify-center gap-1.5 text-xs text-emerald-700">
                <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Assurance RC Pro + garantie décennale vérifiées
              </p>
            </div>
          )}

          {/* ═══ STEP 2: Combined details (urgency + budget + description) ═══ */}
          {step === STEP_DETAILS && (
            <div>
              <button onClick={() => goBack(STEP_SERVICE)} className="mb-6 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600">
                <ArrowLeft className="h-4 w-4" /> Retour
              </button>

              <h2 className="text-center text-2xl font-bold text-slate-900">Quelques détails sur votre projet</h2>
              <p className="mt-2 text-center text-sm text-slate-500">Plus de précisions = devis plus précis · Tout est optionnel</p>

              {/* Urgency chips */}
              <div className="mt-8">
                <p className="text-sm font-semibold text-slate-700">Quand en avez-vous besoin ?</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {URGENCIES.map((u) => {
                    const active = selectedUrgency === u.value
                    return (
                      <button
                        key={u.value}
                        type="button"
                        onClick={() => setSelectedUrgency(u.value)}
                        className={`flex items-center gap-2 rounded-xl border-2 p-3 text-left transition-all ${active ? u.activeColor : u.color}`}
                      >
                        <span className="text-lg">{u.emoji}</span>
                        <div className="min-w-0">
                          <span className="block text-xs font-semibold leading-tight">{u.label}</span>
                          <span className={`text-[10px] ${active ? 'text-white/70' : 'opacity-60'}`}>{u.sublabel}</span>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Budget chips */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-slate-700">Budget approximatif <span className="text-xs font-normal text-slate-400">(optionnel)</span></p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {BUDGETS.map((b) => {
                    const active = selectedBudget === b.value
                    return (
                      <button
                        key={b.value}
                        type="button"
                        onClick={() => setSelectedBudget(b.value)}
                        className={`flex flex-col items-start gap-0.5 rounded-xl border-2 p-3 text-left transition-all ${
                          active ? 'border-primary bg-primary text-white shadow-md shadow-primary/20' : 'border-slate-200 bg-white text-slate-700 hover:border-primary/30'
                        }`}
                      >
                        <span className="text-xs font-semibold">{b.label}</span>
                        <span className={`text-[10px] ${active ? 'text-white/70' : 'text-slate-400'}`}>{b.sublabel}</span>
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Description textarea */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-slate-700">Décrivez votre projet <span className="text-xs font-normal text-slate-400">(optionnel)</span></p>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={3}
                  placeholder="Ex: Refaire la peinture du salon (25m²), murs en bon état, quelques fissures à reboucher..."
                  className="mt-2 w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              {/* Photo upload (collapsible / compact) */}
              <div className="mt-4">
                {photos.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {photos.map((photo, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={photo.url}
                          alt={photo.name}
                          width={64}
                          height={64}
                          loading="lazy"
                          decoding="async"
                          className="h-16 w-16 rounded-xl object-cover border-2 border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            URL.revokeObjectURL(photo.url)
                            setPhotos(prev => prev.filter((_, j) => j !== i))
                          }}
                          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-500 hover:border-primary/40 hover:bg-primary/5">
                  <Camera className="h-4 w-4" />
                  {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? 's' : ''} — ajouter plus` : 'Ajouter une photo (optionnel)'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={async (e) => {
                    const files = e.target.files
                    if (!files) return
                    for (const f of Array.from(files).filter(f => f.size <= 5 * 1024 * 1024).slice(0, 5)) {
                      const preview = URL.createObjectURL(f)
                      const fd = new FormData()
                      fd.append('file', f)
                      try {
                        const res = await fetch('/api/upload', { method: 'POST', body: fd })
                        const data = await res.json() as any
                        setPhotos(prev => [...prev, { name: f.name, url: preview, file: f, uploaded: data.url ?? '' }].slice(0, 5))
                      } catch {
                        setPhotos(prev => [...prev, { name: f.name, url: preview, file: f }].slice(0, 5))
                      }
                    }
                  }} />
                </label>
              </div>

              <button
                type="button"
                onClick={() => goToStep(STEP_CONTACT)}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 text-sm font-bold text-white shadow-md shadow-primary/20 hover:bg-primary/90 transition-all"
              >
                Continuer <ArrowRight className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* ═══ CONTACT (final) ═══ */}
          {step === STEP_CONTACT && (
            <Form method="post">
              <button type="button" onClick={() => goBack(STEP_DETAILS)} className="mb-6 flex items-center gap-1 text-sm text-slate-400 hover:text-slate-600">
                <ArrowLeft className="h-4 w-4" /> Retour
              </button>

              <h2 className="text-center text-2xl font-bold text-slate-900">Dernière étape !</h2>
              <p className="mt-2 text-center text-sm text-slate-500">Vos coordonnées pour recevoir les devis</p>

              <input type="hidden" name="service" value={selectedService} />
              <input type="hidden" name="urgency" value={selectedUrgency} />
              <input type="hidden" name="budget" value={selectedBudget} />
              <input type="hidden" name="description" value={description} />
              <input type="hidden" name="subAnswers" value={JSON.stringify(subAnswers)} />
              <input type="hidden" name="photoUrls" value={JSON.stringify(photos.filter(p => p.uploaded).map(p => p.uploaded))} />

              {actionData?.error && (
                <div className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{actionData.error}</div>
              )}

              <div className="mt-8 space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700">Votre nom</label>
                  <input id="name" name="name" type="text" placeholder="Jean Dupont"
                    autoComplete="name"
                    className="mt-1 w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-slate-700">Téléphone *</label>
                  <div className="relative mt-1">
                    <Phone className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input id="phone" name="phone" type="tel" required placeholder="06 12 34 56 78"
                      autoComplete="tel"
                      inputMode="tel"
                      onChange={handlePhoneChange}
                      className="w-full rounded-xl border-2 border-slate-200 py-3 pl-12 pr-4 text-lg font-medium text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700">Email *</label>
                  <input id="email" name="email" type="email" required placeholder="jean@email.com"
                    autoComplete="email"
                    inputMode="email"
                    className="mt-1 w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                  <p className="mt-1 text-xs text-slate-400">Indispensable pour recevoir vos devis par écrit.</p>
                </div>

                <div>
                  <label htmlFor="postalCode" className="block text-sm font-medium text-slate-700">Code postal *</label>
                  <div className="relative mt-1">
                    <MapPin className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input id="postalCode" name="postalCode" type="text" required maxLength={5} pattern="[0-9]{5}" placeholder="75001"
                      autoComplete="postal-code"
                      inputMode="numeric"
                      defaultValue={preselectedPostal}
                      onChange={handlePostalCodeChange}
                      className="w-full rounded-xl border-2 border-slate-200 py-3 pl-12 pr-4 text-lg font-medium text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
                  </div>
                  {detectedCity && (
                    <p className="mt-1.5 flex items-center gap-1 text-xs text-emerald-600 animate-fade-in">
                      <CheckCircle className="h-3 w-3" /> {detectedCity} détecté
                    </p>
                  )}
                </div>
              </div>

              {/* Recap */}
              <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Récapitulatif</p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {serviceDef && <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">{serviceDef.emoji} {serviceDef.name}</span>}
                  {Object.entries(subAnswers).map(([k, v]) => (
                    <span key={k} className="rounded-full bg-slate-200 px-3 py-1 text-xs text-slate-600">{v}</span>
                  ))}
                  {selectedUrgency && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs text-amber-700">{URGENCIES.find(u => u.value === selectedUrgency)?.label}</span>}
                  {selectedBudget && selectedBudget !== 'unknown' && <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs text-emerald-700">{BUDGETS.find(b => b.value === selectedBudget)?.label}</span>}
                  {photos.length > 0 && <span className="rounded-full bg-blue-100 px-3 py-1 text-xs text-blue-700">📷 {photos.length} photo{photos.length > 1 ? 's' : ''}</span>}
                </div>
              </div>

              {deptDevisCount >= 3 && (
                <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-xs text-emerald-700">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                  </span>
                  <strong>{deptDevisCount}</strong> demande{deptDevisCount > 1 ? 's' : ''} dans votre département cette semaine
                </div>
              )}

              <button type="submit" disabled={isSubmitting}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-primary py-4 text-lg font-bold text-white shadow-lg shadow-primary/25 hover:bg-primary/90 transition-all hover:scale-[1.01] disabled:opacity-60">
                {isSubmitting ? (
                  <><span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> Envoi en cours...</>
                ) : (
                  <>Recevoir mes devis gratuits <ArrowRight className="h-5 w-5" /></>
                )}
              </button>

              <p className="mt-3 text-center text-[11px] text-slate-400">
                🔒 Vos données sont protégées · 100% gratuit · Sans engagement
              </p>
            </Form>
          )}

        </div>
      </main>
    </div>
  )
}
